import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Row, Col, Button } from 'react-bootstrap';
import SearchBox from 'components/common/SearchBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { TaskBreadcrumbItems } from 'data/commonData';
import {
  GET_ALL_TASKS,
  GET_FILTERED_TASKS,
  TaskResponse,
  DELETE_TASK,
  DUPLICATE_TASK,
  MARK_AS_COMPLETED
} from 'services/api/task';
import { GET_EMPLOYEES_QUERY } from 'services/api/employee';
import { GET_JOBS_QUERY, Job } from 'services/api/job';
import { Employee } from 'data/users';
import { taskColumns } from './columns/taskColumns';
import TaskModal from './components/TaskModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import TaskTabs from './components/TaskTabs';
import TaskFilters from './components/TaskFilters';
import TaskViewToggle from './components/TaskViewToggle';
import TaskKanban from './components/TaskKanban';
import { TaskFilters as TaskFiltersType } from './types/activityLog';
import { useAccessToken } from 'providers/AccessTokenProvider';
import { useTaskDetails } from 'providers/TaskDetailsProvider';
import toast from 'react-hot-toast';
import { Outlet, useParams } from 'react-router-dom';
import LoadingPlaceholder from 'components/loading-placeholder/LoadingPlaceholder';
import { useGlobalSocket } from 'hooks/useGlobalSocket';
import { useNotificationSocket } from 'hooks/useNotificationSocket';

const Tasks = () => {
  const { taskId } = useParams();
  const { openTask } = useTaskDetails();
  const queryClient = useQueryClient();
  const { authUser } = useAccessToken();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedTask, setSelectedTask] = useState<TaskResponse | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [isFiltered, setIsFiltered] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');
  const [taskToDelete, setTaskToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [filters, setFilters] = useState<TaskFiltersType>({
    priority: '',
    status: '',
    dueDate: '',
    sortBy: '',
    sortOrder: '',
    creator: '',
    assignedTo: ''
  });

  // Global socket for task events
  useGlobalSocket({
    enabled: Boolean(authUser),
    onTaskCreated: taskData => {
      console.log('Task created via socket in Tasks page:', taskData);
      queryClient.invalidateQueries(['tasks']);
      queryClient.invalidateQueries(['filteredTasks']);
      toast.success(`New task "${taskData.task}" has been created`);
    },
    onTaskUpdated: taskData => {
      console.log('Task updated via socket in Tasks page:', taskData);
      queryClient.invalidateQueries(['tasks']);
      queryClient.invalidateQueries(['filteredTasks']);
      const taskDataId = (taskData as any).id || taskData._id;
      if (taskDataId) {
        queryClient.invalidateQueries(['task', taskDataId]);
      }
    }
  });

  useNotificationSocket({
    enabled: Boolean(authUser),
    onNewNotification: notification => {
      console.log('New notification received in Tasks page:', notification);
      queryClient.invalidateQueries(['notifications']);
    },
    onNotificationCountUpdate: data => {
      queryClient.setQueryData(['notificationCount'], () => ({
        data: { count: data.count }
      }));
    }
  });

  const {
    data: allTasks = [],
    isLoading: isLoadingAllTasks,
    error: allTasksError,
    refetch: refetchAllTasks
  } = useQuery({
    queryKey: ['tasks'],
    queryFn: GET_ALL_TASKS,
    staleTime: 0, // Set to 0 to always refetch when needed
    select: (response: any) => response.data,
    enabled: !isFiltered,
    refetchOnWindowFocus: false
  });

  const {
    data: filteredTasks = [],
    isLoading: isLoadingFilteredTasks,
    error: filteredTasksError,
    refetch: refetchFilteredTasks
  } = useQuery({
    queryKey: ['filteredTasks', filters],
    queryFn: () => GET_FILTERED_TASKS(filters),
    staleTime: 0, // Set to 0 to always refetch when needed
    select: (response: any) => response.data,
    enabled: isFiltered,
    refetchOnWindowFocus: false
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: GET_EMPLOYEES_QUERY,
    staleTime: 3 * 60 * 60 * 1000,
    select: (response: any) => {
      return response.data.map((employee: Employee) => ({
        value: (employee as any).id || employee._id,
        label: `${employee.firstName} ${employee.lastName}`
      }));
    }
  });

  const { data: jobs = [] } = useQuery({
    queryKey: ['jobs'],
    queryFn: GET_JOBS_QUERY,
    staleTime: 3 * 60 * 60 * 1000,
    select: (response: any) => {
      return response?.data
        ?.filter(
          (job: Job) =>
            job.jobStage?.name?.toLowerCase() !== 'closed' &&
            job.jobStage?.name?.toLowerCase() !== 'cancelled'
        )
        .map((job: Job) => ({
          value: (job as any).id || job._id,
          label: job.title,
          well: job.well,
          location: job.location,
          customer: job.customer,
          jobNumber: job.jobNumber
        }));
    }
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: GET_EMPLOYEES_QUERY,
    staleTime: 3 * 60 * 60 * 1000,
    select: (response: any) => {
      return response.data
        .map((employee: Employee) => ({
          _id: (employee as any).id || employee._id,
          firstName: employee.firstName,
          lastName: employee.lastName,
          email: employee.email
        }))
        .sort((a: Employee, b: Employee) => {
          const aName = `${a.firstName} ${a.lastName}`;
          const bName = `${b.firstName} ${b.lastName}`;
          return aName.localeCompare(bName);
        });
    }
  });

  const deleteTaskMutation = useMutation(DELETE_TASK, {
    onSuccess: () => {
      queryClient.invalidateQueries(['tasks']);
      queryClient.invalidateQueries(['filteredTasks']);
      queryClient.invalidateQueries(['activityLogs']);
      toast.success('Task deleted successfully');
      setShowDeleteModal(false);
      setTaskToDelete(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.messages || 'Failed to delete task');
    }
  });

  const duplicateTaskMutation = useMutation(DUPLICATE_TASK, {
    onSuccess: () => {
      queryClient.invalidateQueries(['tasks']);
      queryClient.invalidateQueries(['filteredTasks']);
      queryClient.invalidateQueries(['activityLogs']);
      toast.success('Task duplicated successfully');
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.messages || 'Failed to duplicate task'
      );
    }
  });

  const markAsCompletedMutation = useMutation(MARK_AS_COMPLETED, {
    onSuccess: () => {
      queryClient.invalidateQueries(['tasks']);
      queryClient.invalidateQueries(['filteredTasks']);
      queryClient.invalidateQueries(['activityLogs']);
      toast.success('Task status updated');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.messages || 'Failed to update task');
    }
  });

  const tasks = isFiltered ? filteredTasks : allTasks;

  const handleCreateTask = () => {
    setSelectedTask(null);
    setShowModal(true);
  };

  const handleEditTask = (task: TaskResponse) => {
    setSelectedTask(task);
    setShowModal(true);
  };

  const handleDeleteTask = (task: TaskResponse) => {
    setTaskToDelete({
      id: (task as any).id || task._id,
      name: task.task
    });
    setShowDeleteModal(true);
  };

  const confirmDeleteTask = () => {
    if (taskToDelete) {
      deleteTaskMutation.mutate(taskToDelete.id);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setTaskToDelete(null);
  };

  const handleCompleteTask = (taskId: string) => {
    markAsCompletedMutation.mutate(taskId);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTask(null);
  };

  const handleFilterChange = (name: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyFilters = () => {
    const hasActiveFilters =
      filters.priority !== '' ||
      filters.status !== '' ||
      filters.dueDate !== '' ||
      filters.creator !== '' ||
      filters.assignedTo !== '';

    setIsFiltered(hasActiveFilters);

    if (hasActiveFilters) {
      refetchFilteredTasks();
    }
  };

  const handleResetFilters = () => {
    setFilters({
      priority: '',
      status: '',
      dueDate: '',
      sortBy: 'dueDate',
      sortOrder: 'asc',
      creator: '',
      assignedTo: ''
    });
    setIsFiltered(false);
  };

  const handleDuplicateTask = (taskId: string) => {
    duplicateTaskMutation.mutate(taskId);
  };

  const handleViewTask = (taskId: string) => {
    openTask(taskId);
  };

  const handleViewChange = (view: 'list' | 'kanban') => {
    setViewMode(view);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchTerm(e.target.value);

  const handleTabChange = (tab: string) => {
    if (tab === 'tasks') {
      if (isFiltered) {
        refetchFilteredTasks();
      } else {
        refetchAllTasks();
      }
    }
  };

  const columns = taskColumns({
    onEdit: handleEditTask,
    onDelete: (taskId: string) => {
      const task = tasks.find(
        (t: TaskResponse) => ((t as any).id || t._id) === taskId
      );
      if (task) {
        handleDeleteTask(task);
      }
    },
    onComplete: handleCompleteTask,
    onDuplicate: handleDuplicateTask,
    onView: handleViewTask
  });

  const isLoading = isLoadingAllTasks || isLoadingFilteredTasks;
  const error = allTasksError || filteredTasksError;

  const displayedTasks = tasks
    ?.filter((task: TaskResponse) =>
      task?.task?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    )
    .sort((a: TaskResponse, b: TaskResponse) => {
      return a.task.localeCompare(b.task);
    });
  if (taskId) return <Outlet />;

  return (
    <div className="py-4">
      <PageBreadcrumb items={TaskBreadcrumbItems} />
      <Row className="align-items-center justify-content-between mb-4">
        <Col>
          <h2 className="mb-3 mb-sm-0 fw-bold">Tasks</h2>
        </Col>
        <Col xs="auto" className="d-flex align-items-center">
          <TaskViewToggle view={viewMode} onChange={handleViewChange} />
          <Button variant="primary" className="ms-2" onClick={handleCreateTask}>
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            Add new task
          </Button>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col>
          <SearchBox
            placeholder="Search tasks"
            value={searchTerm}
            onChange={handleSearchChange}
            className="me-2"
          />
        </Col>
        <Col xs="auto">
          {viewMode === 'list' && (
            <TaskFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
              users={users}
            />
          )}
        </Col>
      </Row>

      {isLoading ? (
        <LoadingPlaceholder />
      ) : error ? (
        <div className="alert alert-danger">{error as string}</div>
      ) : (
        <>
          {viewMode === 'list' ? (
            <TaskTabs
              taskData={displayedTasks}
              taskColumns={columns}
              isLoadingTasks={isLoading}
              tasksError={error as string}
              onTabChange={handleTabChange}
            />
          ) : (
            <TaskKanban
              tasks={displayedTasks}
              onEdit={handleEditTask}
              onDelete={(taskId: string) => {
                const task = tasks.find(
                  (t: TaskResponse) => ((t as any).id || t._id) === taskId
                );
                if (task) {
                  handleDeleteTask(task);
                }
              }}
              onView={handleViewTask}
            />
          )}
        </>
      )}

      <TaskModal
        show={showModal}
        handleClose={handleCloseModal}
        task={selectedTask}
        employees={employees}
        jobs={jobs}
      />

      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        onConfirm={confirmDeleteTask}
        taskName={taskToDelete?.name || ''}
        isDeleting={deleteTaskMutation.isLoading}
      />
    </div>
  );
};

export default Tasks;
